<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import {
  useBrowserStore,
  useItineraryStore,
} from '@apps/main/new-desktop/core/stores';

import { AppButton, AppLinkButton, AppFlicker } from '@libs/ui';

import { useGlobalSuggestionSwitchStore } from '../../stores/globalSuggestionSwitchStore';

import type { GlobalSuggestionSwitchOption } from './globalSuggestionTypeSwitch.model';
import { GlobalSuggestionSwitchType } from './globalSuggestionTypeSwitch.model';

interface GlobalSuggestionTypeSwitchEmits {
  (eventName: 'low-price-calendar-click'): void;
}

const emit = defineEmits<GlobalSuggestionTypeSwitchEmits>();

const globalSuggestionSwitchStore = useGlobalSuggestionSwitchStore();
const browserStore = useBrowserStore();
const itineraryStore = useItineraryStore();

function shouldHighlightLabel(option: GlobalSuggestionSwitchOption): boolean {
  return (
    option.type === GlobalSuggestionSwitchType.LowestPrice &&
    browserStore.isLargerThan1199px
  );
}

function shouldHighlightPrice(option: GlobalSuggestionSwitchOption): boolean {
  return (
    option.type === GlobalSuggestionSwitchType.LowestPrice &&
    !browserStore.isLargerThan1199px
  );
}

function updateSuggestion(suggestionType: GlobalSuggestionSwitchType): void {
  if (globalSuggestionSwitchStore.currentSwitchType !== suggestionType) {
    globalSuggestionSwitchStore.updateSwitchType(suggestionType);
  }
}

function onLowPriceCalendarClick(): void {
  emit('low-price-calendar-click');
}

const suggestionOptions = ref<GlobalSuggestionSwitchOption[]>([]);

const shouldShowGlobalSuggestionSwitch = computed<boolean>(
  () =>
    suggestionOptions.value.every((suggestion) => !!suggestion.price) ||
    itineraryStore.isSearchingGlobalSuggestionItineraries
);

// FYI: Use ref instead of computed, so UI will update only when new value is provided
// as computed in store has more dependencies and can have middle step with empty array when re-calculating
watch(
  () => globalSuggestionSwitchStore.suggestionOptions,
  () => {
    suggestionOptions.value = globalSuggestionSwitchStore.suggestionOptions;
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <div class="global-suggestion-type-switch">
    <template v-if="shouldShowGlobalSuggestionSwitch">
      <AppButton
        v-for="option in suggestionOptions"
        :key="option.type"
        :class="{
          'is-selected': option.isSelected,
        }"
        class="global-suggestion-type-switch-button"
        type="secondary"
        size="medium"
        state="enabled"
        :no-border="true"
        @click="updateSuggestion(option.type)"
      >
        <div
          class="label"
          :class="{
            'is-premade-trip': shouldHighlightLabel(option),
          }"
        >
          {{ option.text }}
          <span
            v-if="option.price"
            class="price"
            :class="{
              'is-premade-trip': shouldHighlightPrice(option),
            }"
          >
            {{ option.price }}
          </span>
          <AppFlicker v-else width="spacing-48" height="font-size-m" />
        </div>
      </AppButton>
    </template>

    <div
      v-if="browserStore.isLargerThan599px && browserStore.isSmallerThan1200px"
      class="low-price-calendar-button"
    >
      <AppLinkButton
        :text="$t('planner.low_price_calendar.button')"
        size="s"
        :bold="false"
        :underline="false"
        left-icon="lowPriceCalendar"
        icon-size="18"
        @click="onLowPriceCalendarClick"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.global-suggestion-type-switch {
  --static-button-width-mobile: 183px;
  --static-button-width: 240px;

  position: relative;
  overflow: hidden;
  width: 100%;
  display: flex;
  padding: var(--spacing-8) var(--spacing-12) 0 var(--spacing-12);
  background-color: var(--color-white);
  justify-content: center;
  @include for-more-or-equal-than-1200-px {
    padding: 0;
    justify-content: start;
  }

  .global-suggestion-type-switch-button {
    border-radius: 0;
    padding: var(--spacing-4) var(--spacing-16) var(--spacing-8)
      var(--spacing-16);
    width: var(--static-button-width);
    @include for-less-or-equal-than-1199-px {
      width: var(--static-button-width-mobile);
    }
  }
  .is-selected {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: var(--spacing-4);
      background-color: var(--color-brand-violet);
      border-radius: var(--border-radius-4);
    }
  }
  .is-premade-trip {
    background-color: var(--color-brand-summer);
    padding: var(--spacing-4) var(--spacing-8);
    border-radius: var(--border-radius-4);
  }
  .label {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-4);

    @include font-label-s-light;

    @include for-less-or-equal-than-1199-px {
      flex-direction: column;
      gap: 0;
    }

    .price {
      @include font-label-m-bold;
    }
  }

  .low-price-calendar-button {
    display: flex;
    align-items: center;
    padding: var(--spacing-4) var(--spacing-16) var(--spacing-8)
      var(--spacing-16);

    @include on-hover {
      background-color: var(--color-neutral-20);
    }
  }
}
</style>
